import type { HTMLAttributes, ReactNode } from 'react';

export type PageFrameContainer = 'mobile-frame' | 'adaptive-app' | 'readable-content' | 'reader-content';

interface PageFrameProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  topInset?: 'page' | 'stack' | 'reader' | 'none';
  bottomInset?: 'tab' | 'none';
  container?: PageFrameContainer;
}

const topInsetClass: Record<NonNullable<PageFrameProps['topInset']>, string> = {
  page: 'pt-[calc(var(--safe-area-top)+var(--page-top-offset))] min-[430px]:pt-[calc(var(--safe-area-top)+var(--page-top-offset-comfortable))]',
  stack:
    'pt-[calc(var(--safe-area-top)+0.875rem)] min-[430px]:pt-[calc(var(--safe-area-top)+1rem)]',
  reader: 'pt-[calc(var(--safe-area-top)+0.5rem)]',
  none: '',
};

const bottomInsetClass: Record<NonNullable<PageFrameProps['bottomInset']>, string> = {
  tab: 'pb-[var(--tab-shell-space)]',
  none: 'pb-0',
};

const containerClass: Record<PageFrameContainer, string> = {
  'mobile-frame': '',
  'adaptive-app': 'min-[768px]:max-w-[var(--desktop-app-max-width)] min-[768px]:px-[var(--desktop-gutter)]',
  'readable-content':
    'min-[768px]:max-w-[var(--content-readable-max-width)] min-[768px]:px-[var(--desktop-gutter)]',
  'reader-content':
    'min-[768px]:max-w-[var(--reader-content-max-width)] min-[768px]:px-[var(--desktop-gutter)]',
};

export function PageFrame({
  children,
  className = '',
  topInset = 'page',
  bottomInset = 'tab',
  container = 'mobile-frame',
  ...props
}: PageFrameProps) {
  return (
    <div
      className={[
        'mx-auto min-h-full w-full max-w-[var(--screen-max-width)] px-[var(--page-gutter)]',
        containerClass[container],
        topInsetClass[topInset],
        bottomInsetClass[bottomInset],
        className,
      ].join(' ')}
      data-page-container={container}
      data-page-frame={`${topInset}:${bottomInset}`}
      {...props}
    >
      {children}
    </div>
  );
}
